import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const MOBILE_BREAKPOINT = '960px';

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1rem;
  padding: 1rem 1.25rem;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 16px;
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.14);
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    flex-direction: column;
    align-items: stretch;
    padding: 0.85rem 0.85rem;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex: 1 1 auto;
  min-width: 0;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
    min-width: 0;
  }
`;

const NavScroll = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  min-width: 0;
  &::-webkit-scrollbar {
    display: none;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
    margin: 0;
    padding: 0;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const NavLink = styled(Link)<{ $active?: boolean }>`
  padding: 0.58rem 0.78rem;
  font-size: 0.9rem;
  color: ${({ $active }) => ($active ? 'var(--primary)' : 'var(--onSurfaceVariant)')};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  text-decoration: none;
  border-radius: 10px;
  border: 1px solid ${({ $active }) => ($active ? 'var(--primary)' : 'transparent')};
  background: ${({ $active }) => ($active ? 'var(--surfaceVariant)' : 'transparent')};
  white-space: nowrap;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  &:hover {
    color: var(--primary);
    background: var(--surfaceVariant);
    border-color: var(--outlineVariant);
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    min-height: 44px;
    display: inline-flex;
    align-items: center;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
    > * {
      width: 100%;
      justify-content: center;
    }
  }
`;

export type MarketingHeaderActiveTab = 'dashboard' | 'keywords' | 'subscribers' | 'messages' | 'funnels' | 'reports' | 'inbox' | 'social';

export interface MarketingHeaderProps {
  activeTab: MarketingHeaderActiveTab;
  rightSlot?: React.ReactNode;
}

export default function MarketingHeader({ activeTab, rightSlot }: MarketingHeaderProps) {
  return (
    <Header>
      <HeaderLeft>
        <NavScroll>
          <Nav>
            <NavLink to="/marketing" $active={activeTab === 'dashboard'}>
            Dashboard
          </NavLink>
          <NavLink to="/marketing/keywords" $active={activeTab === 'keywords'}>
            Keywords
          </NavLink>
          <NavLink to="/marketing/subscribers" $active={activeTab === 'subscribers'}>
            Subscribers
          </NavLink>
          <NavLink to="/marketing/messages" $active={activeTab === 'messages'}>
            Messages
          </NavLink>
          <NavLink to="/marketing/funnel-analytics" $active={activeTab === 'funnels'}>
            Funnels
          </NavLink>
          <NavLink to="/marketing/reports" $active={activeTab === 'reports'}>
            Reports
          </NavLink>
          <NavLink to="/marketing/social" $active={activeTab === 'social'}>
            Social
          </NavLink>
          <NavLink to="/marketing/inbox" $active={activeTab === 'inbox'}>
            Inbox
          </NavLink>
          </Nav>
        </NavScroll>
      </HeaderLeft>
      {rightSlot ? <HeaderRight>{rightSlot}</HeaderRight> : null}
    </Header>
  );
}
